import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridSortModel, GridFilterModel, GridEventListener, GridRowParams } from '@mui/x-data-grid';
import { extractFiltersFromModel, resolveProvinceSearchMatches } from '../../../utils/gridFilterUtils';
import { useAppSelector } from '../../../store/hooks';
import { BaseDataGrid, PageHeader, PersistentTabs, TabConfig, SchoolDialog, ProvinceDialog } from '../../../components';
import { Top149Chart } from './Top149Chart';
import { Top149Toolbar } from './';
import { useTop149Columns, useTop149Query, buildTop149ExportParams, type UseTop149QueryParams, useTop149ProvinceQuery, useTop149MergedProvinceQuery, useTop149ProvincesColumns } from '../hooks';
import { Top149SchoolYearStatsSchema, top149Api } from '../../../services';
import { z } from 'zod';
import { useErrorHandler } from '../../../lib/errorHandler';
import { MOBILE_BREAKPOINT, ROW_HEIGHTS, LIST_HEIGHTS } from '../../../utils/responsive';
import { useEntityListState } from '../../../hooks';

type Top149SchoolStats = z.infer<typeof Top149SchoolYearStatsSchema>;

interface Top149ContentProps {
	isAdmin: boolean;
	canEdit?: boolean;
	yearsAchieveTop149?: number;
	setYearsAchieveTop149?: (year: number | undefined) => void;
	availableYears?: number[];
}



export default function Top149Content({
	isAdmin,
	canEdit = false,
	yearsAchieveTop149,
	setYearsAchieveTop149,
	availableYears = []
}: Top149ContentProps) {
	const { showError } = useErrorHandler();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);
	const mergedProvinces = useAppSelector((state) => state.filters.mergedProvinces);
	const provinces = useAppSelector((state) => state.filters.provinces);

	const {
		searchValue, paginationModel, sortModel, filterModel,
		handlePaginationModelChange, handleSortModelChange,
		handleFilterModelChange, handleSearch,
	} = useEntityListState({ defaultSortField: 'tenTruong' });

	// Province tabs state
	const [provinceSortModel, setProvinceSortModel] = useState<GridSortModel>([{ field: 'tenTinh', sort: 'asc' }]);
	const [provinceFilterModel, setProvinceFilterModel] = useState<GridFilterModel>({ items: [] });
	const [provinceSearchValue, setProvinceSearchValue] = useState('');

	const [mergedProvinceSortModel, setMergedProvinceSortModel] = useState<GridSortModel>([{ field: 'tenTinh', sort: 'asc' }]);
	const [mergedProvinceFilterModel, setMergedProvinceFilterModel] = useState<GridFilterModel>({ items: [] });
	const [mergedProvinceSearchValue, setMergedProvinceSearchValue] = useState('');

	const [schoolDialog, setSchoolDialog] = useState<{
		open: boolean;
		school: Top149SchoolStats | null;
	}>({ open: false, school: null });

	const [provinceDialog, setProvinceDialog] = useState<{
		open: boolean;
		maTinh: string | null;
		tenTinh: string | null;
		merged: boolean;
	}>({ open: false, maTinh: null, tenTinh: null, merged: false });

	const schoolDialogRef = useRef(schoolDialog);
	schoolDialogRef.current = schoolDialog;
	const provinceDialogRef = useRef(provinceDialog);
	provinceDialogRef.current = provinceDialog;

	const handleYearChange = useCallback((event: SelectChangeEvent<number>) => {
		const value = event.target.value as number;
		setYearsAchieveTop149?.(value);
	}, [setYearsAchieveTop149]);

	const extractedFilters = useMemo(() =>
		extractFiltersFromModel(filterModel, mergedProvinces, provinces),
		[filterModel, mergedProvinces, provinces]
	);

	const hasSearchValue = searchValue.trim().length > 0;
	// Ô tìm kiếm phải hiểu cả tên tỉnh gốc lẫn tên tỉnh sau sáp nhập: cột "Sau
	// sáp nhập" trên lưới hiển thị tên từ danh mục mới, nên gõ đúng tên đang
	// thấy trên màn hình phải ra kết quả.
	const provinceSearchMatches = useMemo(
		() => resolveProvinceSearchMatches(searchValue, provinces, mergedProvinces),
		[searchValue, provinces, mergedProvinces]
	);
	const hasProvinceNameMatch = provinceSearchMatches.maTinh.length > 0 || provinceSearchMatches.maTinhMoi.length > 0;

	const resolvedMaTinh = extractedFilters.maTinh ?? (provinceSearchMatches.maTinh.length > 0 ? provinceSearchMatches.maTinh : undefined);
	const resolvedMaTinhMoi = extractedFilters.maTinhMoi ?? (provinceSearchMatches.maTinhMoi.length > 0 ? provinceSearchMatches.maTinhMoi : undefined);
	const resolvedQuery = hasProvinceNameMatch ? undefined : (hasSearchValue ? searchValue : undefined);
	// Chỉ OR khi CẢ HAI mã đến từ chính ô tìm kiếm tự do (không có bộ lọc cột
	// nào đang chủ động) — bộ lọc theo cột (tenTinh/tenTinhSauSapNhap) vẫn AND.
	const provinceQueryOr = hasProvinceNameMatch && !extractedFilters.maTinh && !extractedFilters.maTinhMoi;

	// Use query hook with pagination and sorting
	const queryParams: UseTop149QueryParams = {
		filters: {
			query: resolvedQuery,
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined,
			maTinh: resolvedMaTinh
		},
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
		sortOrder: (sortModel.length > 0 ? sortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
		yearsAchieveTop149,
		maTinhMoi: resolvedMaTinhMoi,
		hasCodeNameChange: extractedFilters.hasCodeNameChange,
		provinceQueryOr,
	};
	const { data: schoolsData, isLoading, refetch } = useTop149Query(queryParams);
	const exportParams = buildTop149ExportParams(queryParams);

	const schools = useMemo(() => schoolsData?.data?.stats || [], [schoolsData?.data?.stats]);
	const totalCount = schoolsData?.data?.total || 0;

	// Province queries
	const provinceFilters = useMemo(() => {
		const filters: { maTinh?: string; tenTinh?: string } = {};
		provinceFilterModel.items.forEach(item => {
			if (item.field === 'maTinh' && item.value) filters.maTinh = item.value;
			if (item.field === 'tenTinh' && item.value) filters.tenTinh = item.value;
		});
		if (provinceSearchValue) {
			filters.tenTinh = provinceSearchValue;
		}
		return filters;
	}, [provinceFilterModel, provinceSearchValue]);

	const mergedProvinceFilters = useMemo(() => {
		const filters: { maTinh?: string; tenTinh?: string } = {};
		mergedProvinceFilterModel.items.forEach(item => {
			if (item.field === 'maTinh' && item.value) filters.maTinh = item.value;
			if (item.field === 'tenTinh' && item.value) filters.tenTinh = item.value;
		});
		if (mergedProvinceSearchValue) {
			filters.tenTinh = mergedProvinceSearchValue;
		}
		return filters;
	}, [mergedProvinceFilterModel, mergedProvinceSearchValue]);

	const { data: provinceData, isLoading: isLoadingProvince, refetch: refetchProvince } = useTop149ProvinceQuery({
		year: yearsAchieveTop149,
		sortBy: provinceSortModel.length > 0 ? provinceSortModel[0].field : undefined,
		sortOrder: (provinceSortModel.length > 0 ? provinceSortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
		maTinh: provinceFilters.maTinh,
		tenTinh: provinceFilters.tenTinh,
		enabled: yearsAchieveTop149 !== undefined
	});

	const { data: mergedProvinceData, isLoading: isLoadingMergedProvince, refetch: refetchMergedProvince } = useTop149MergedProvinceQuery({
		year: yearsAchieveTop149,
		sortBy: mergedProvinceSortModel.length > 0 ? mergedProvinceSortModel[0].field : undefined,
		sortOrder: (mergedProvinceSortModel.length > 0 ? mergedProvinceSortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
		maTinh: mergedProvinceFilters.maTinh,
		tenTinh: mergedProvinceFilters.tenTinh,
		enabled: yearsAchieveTop149 !== undefined
	});

	const provinceStats = useMemo(() => provinceData?.data?.stats || [], [provinceData?.data?.stats]);
	const mergedProvinceStats = useMemo(() => mergedProvinceData?.data?.stats || [], [mergedProvinceData?.data?.stats]);

	// Sync URL params with dialog state
	useEffect(() => {
		const dialogType = searchParams.get('dialog') as 'school' | 'province' | null;
		const id = searchParams.get('id');
		const merged = searchParams.get('merged') === 'true';

		if (dialogType === 'school' && id && schools.length > 0) {
			if (schoolDialogRef.current.open && schoolDialogRef.current.school?.maTruong === id) {
				return;
			}
			const school = schools.find((s) => s.maTruong === id);
			if (school) {
				setSchoolDialog({
					open: true,
					school,
				});
			}
		} else if (dialogType === 'province' && id) {
			if (provinceDialogRef.current.open && provinceDialogRef.current.maTinh === id) {
				return;
			}
			const provinceList = merged ? mergedProvinceStats : provinceStats;
			const province = provinceList.find((p) => p.maTinh === id);
			if (province) {
				setProvinceDialog({
					open: true,
					maTinh: province.maTinh,
					tenTinh: province.tenTinh,
					merged,
				});
			}
		} else if (!dialogType) {
			if (schoolDialogRef.current.open) {
				setSchoolDialog({ open: false, school: null });
			}
			if (provinceDialogRef.current.open) {
				setProvinceDialog({ open: false, maTinh: null, tenTinh: null, merged: false });
			}
		}
	}, [searchParams, schools, provinceStats, mergedProvinceStats]);

	// Note: Top149 schools are read-only - managed via THPT module
	// No delete mutation needed here

	const handleRefresh = useCallback(() => {
		// Prevent multiple rapid calls
		if (isLoading) return;

		refetch();
		refetchProvince();
		refetchMergedProvince();
	}, [isLoading, refetch, refetchProvince, refetchMergedProvince]);

	const handleRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
		setSearchParams({ dialog: 'school', id: (params.row as Top149SchoolStats).maTruong });
	}, [setSearchParams]);

	const handleCloseSchoolDialog = useCallback(() => {
		setSearchParams({});
	}, [setSearchParams]);

	const handleProvinceRowClick = useCallback((params: GridRowParams, merged: boolean = false) => {
		setSearchParams({ dialog: 'province', id: params.row.maTinh, merged: String(merged) });
	}, [setSearchParams]);

	const handleCloseProvinceDialog = useCallback(() => {
		setSearchParams({});
	}, [setSearchParams]);

	const handleProvinceSortModelChange = useCallback((newModel: GridSortModel) => {
		setProvinceSortModel(newModel);
	}, []);

	const handleProvinceFilterModelChange = useCallback((newModel: GridFilterModel) => {
		setProvinceFilterModel(newModel);
	}, []);

	const handleMergedProvinceSortModelChange = useCallback((newModel: GridSortModel) => {
		setMergedProvinceSortModel(newModel);
	}, []);

	const handleMergedProvinceFilterModelChange = useCallback((newModel: GridFilterModel) => {
		setMergedProvinceFilterModel(newModel);
	}, []);

	const handleProvinceSearch = useCallback((value: string) => {
		setProvinceSearchValue(value);
	}, []);

	const handleMergedProvinceSearch = useCallback((value: string) => {
		setMergedProvinceSearchValue(value);
	}, []);

	const handleExportProvinceStats = useCallback(async () => {
		if (!yearsAchieveTop149) {
			showError('Vui lòng chọn năm để xuất Excel');
			return;
		}
		try {
			const sortBy = provinceSortModel.length > 0 ? provinceSortModel[0].field : undefined;
			const sortOrder = provinceSortModel.length > 0 ? provinceSortModel[0].sort || undefined : undefined;
			await top149Api.exportProvinceStatsToExcel({ year: yearsAchieveTop149, sortBy, sortOrder, ...provinceFilters });
		} catch (error) {
			console.error('[ERROR] Export province stats error:', error);
			showError('Có lỗi xảy ra khi tải xuống file Excel. Vui lòng thử lại.');
		}
	}, [yearsAchieveTop149, provinceSortModel, provinceFilters, showError]);

	const handleExportMergedProvinceStats = useCallback(async () => {
		if (!yearsAchieveTop149) {
			showError('Vui lòng chọn năm để xuất Excel');
			return;
		}
		try {
			const sortBy = mergedProvinceSortModel.length > 0 ? mergedProvinceSortModel[0].field : undefined;
			const sortOrder = mergedProvinceSortModel.length > 0 ? mergedProvinceSortModel[0].sort || undefined : undefined;
			await top149Api.exportProvinceStatsToExcel({ year: yearsAchieveTop149, sortBy, sortOrder, ...mergedProvinceFilters }, true);
		} catch (error) {
			console.error('[ERROR] Export merged province stats error:', error);
			showError('Có lỗi xảy ra khi tải xuống file Excel. Vui lòng thử lại.');
		}
	}, [yearsAchieveTop149, mergedProvinceSortModel, mergedProvinceFilters, showError]);

	const columns = useTop149Columns({
		isAdmin: false,
		isMobile,
		admissionStats: schoolsData?.data,
		onEdit: () => () => { },
		onDelete: () => () => { }
	});

	const provinceColumns = useTop149ProvincesColumns({
		provinceStats: provinceData?.data,
		year: yearsAchieveTop149
	});

	const mergedProvinceColumns = useTop149ProvincesColumns({
		provinceStats: mergedProvinceData?.data,
		year: yearsAchieveTop149
	});

	const tabs: TabConfig[] = [
		{
			label: 'Danh sách trường',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={schools}
						columns={columns}
						loading={isLoading}
						onRowClick={handleRowClick}
						getRowId={(row) => row.maTruong}
						rowCount={totalCount}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
						filterModel={filterModel}
						onFilterModelChange={handleFilterModelChange}
						pageSizeOptions={[10, 25, 50, 100]}
						paginationMode="server"
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<Top149Toolbar
									searchValue={searchValue}
									onSearch={handleSearch}
									onRefresh={handleRefresh}
									exportParams={exportParams}
									searchPlaceholder="Tìm trường hoặc tỉnh/thành..."
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Thống kê theo tỉnh (trước sáp nhập)',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={provinceStats}
						columns={provinceColumns}
						loading={isLoadingProvince}
						getRowId={(row) => row.maTinh}
						onRowClick={(params) => handleProvinceRowClick(params, false)}
						sortModel={provinceSortModel}
						onSortModelChange={handleProvinceSortModelChange}
						filterModel={provinceFilterModel}
						onFilterModelChange={handleProvinceFilterModelChange}
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick={false}
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<Top149Toolbar
									searchValue={provinceSearchValue}
									onSearch={handleProvinceSearch}
									onRefresh={handleRefresh}
									searchPlaceholder="Tìm kiếm tỉnh/thành..."
									showExport={true}
									onExport={handleExportProvinceStats}
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Thống kê theo tỉnh (sau sáp nhập)',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={mergedProvinceStats}
						columns={mergedProvinceColumns}
						loading={isLoadingMergedProvince}
						getRowId={(row) => row.maTinh}
						onRowClick={(params) => handleProvinceRowClick(params, true)}
						sortModel={mergedProvinceSortModel}
						onSortModelChange={handleMergedProvinceSortModelChange}
						filterModel={mergedProvinceFilterModel}
						onFilterModelChange={handleMergedProvinceFilterModelChange}
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick={false}
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<Top149Toolbar
									searchValue={mergedProvinceSearchValue}
									onSearch={handleMergedProvinceSearch}
									onRefresh={handleRefresh}
									searchPlaceholder="Tìm kiếm tỉnh/thành..."
									showExport={true}
									onExport={handleExportMergedProvinceStats}
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Biểu đồ thống kê',
			content: (
				<Box sx={{
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					overflow: 'auto'
				}}>
					<Top149Chart height={500} yearsAchieveTop149={yearsAchieveTop149} />
				</Box>
			)
		}
	];

	return (
		<Box sx={{
			width: '100%',
			height: { xs: '100%', md: '100vh' },
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			p: 0
		}}>
			<PageHeader
				title="Danh sách Trường Top 149"
				onRefresh={handleRefresh}
				isAdmin={isAdmin}
				loading={isLoading}
			/>

			<Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
				<FormControl size="small" sx={{ minWidth: 200, width: { xs: '100%', sm: 200 } }}>
					<InputLabel id="years-achieve-top149-label">Năm đạt Top 149</InputLabel>
					<Select
						labelId="years-achieve-top149-label"
						id="years-achieve-top149-select"
						value={yearsAchieveTop149 ?? ''}
						label="Năm đạt Top 149"
						onChange={handleYearChange}
					>
						{availableYears.map((year) => (
							<MenuItem key={year} value={year}>
								{year}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			<PersistentTabs
				pageKey="top149"
				tabs={tabs}
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					minHeight: 0
				}}
			/>

			<SchoolDialog
				open={schoolDialog.open}
				onClose={handleCloseSchoolDialog}
				school={schoolDialog.school ? {
					maTruong: schoolDialog.school.maTruong,
					tenTruong: schoolDialog.school.tenTruong,
					maTinh: '',
					maNoiBo: null,
					top149: true,
					ghiChu: schoolDialog.school.ghiChu ?? null,
					createdAt: '',
					updatedAt: '',
					tinhThanh: schoolDialog.school.tenTinh ? {
						maTinh: '',
						tenTinh: schoolDialog.school.tenTinh
					} : undefined
				} : null}
				mode="edit"
				disabled={!canEdit}
			/>

			<ProvinceDialog
				open={provinceDialog.open}
				onClose={handleCloseProvinceDialog}
				province={provinceDialog.maTinh ? {
					maTinh: provinceDialog.maTinh,
					tenTinh: provinceDialog.tenTinh || '',
					maTinhMoi: null,
					soTruong: 0,
					soTruongTop149: 0,
					createdAt: '',
					updatedAt: ''
				} : null}
				mode="view"
				year={yearsAchieveTop149}
				top149Only={true}
				merged={provinceDialog.merged}
			/>
		</Box>
	);
}